import React, { useMemo } from "react";
import { createPortal } from "react-dom";

const Z_BACKDROP = 10085;
const Z_BLOCK = 10086;
/** Boven menu-sheet (10100) en header (10000) */
const Z_CARD = 10200;

const SCREEN_INSET = 12;
const INNER_PAD = 8;
const CORNER_RX = 18;

/**
 * @param {DOMRect} rect
 * @param {number} vw
 * @param {number} vh
 */
export function computeTutorialHighlightBox(rect, vw, vh) {
  if (!rect || rect.width < 4 || rect.height < 4) return null;
  let left = rect.left + INNER_PAD;
  let top = rect.top + INNER_PAD;
  let width = rect.width - 2 * INNER_PAD;
  let height = rect.height - 2 * INNER_PAD;
  left = Math.max(SCREEN_INSET, left);
  top = Math.max(SCREEN_INSET, top);
  width = Math.min(width, vw - left - SCREEN_INSET);
  height = Math.min(height, vh - top - SCREEN_INSET);
  width = Math.max(48, width);
  height = Math.max(48, height);
  return { left, top, width, height, rx: CORNER_RX };
}

/** Transparante vangvlakken: klikken buiten het spotlight-gat worden tegengehouden (geen blur). */
function SharpPointerBlockers({ hole, windowSize }) {
  if (!hole || !windowSize) return null;
  const { w, h } = windowSize;
  const { left, top, width, height } = hole;
  const panels = [
    { top: 0, left: 0, width: w, height: Math.max(0, top) },
    { top: top + height, left: 0, width: w, height: Math.max(0, h - top - height) },
    { top, left: 0, width: Math.max(0, left), height },
    { top, left: left + width, width: Math.max(0, w - left - width), height },
  ];

  return (
    <>
      {panels.map((p, i) => (
        <div
          key={i}
          style={{
            position: "fixed",
            top: p.top,
            left: p.left,
            width: p.width,
            height: p.height,
            zIndex: Z_BLOCK,
            background: "transparent",
            pointerEvents: "auto",
            boxSizing: "border-box",
          }}
          aria-hidden
        />
      ))}
    </>
  );
}

function FullBlurLayer() {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: Z_BACKDROP,
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
        background: "rgba(255, 255, 255, 0.45)",
        pointerEvents: "auto",
        boxSizing: "border-box",
        transform: "translateZ(0)",
      }}
    />
  );
}

/**
 * Viewport minus één of meer rechthoeken: panelen voor blur/blockers.
 * @param {{ w: number, h: number }} windowSize
 * @param {Array<{left:number,top:number,width:number,height:number}>} excludeRects
 */
/** Snijdt een hole naar het zichtbare viewport (zelfde coördinatensysteem als getBoundingClientRect). */
function clipHoleToViewport(raw, vw, vh) {
  if (!raw || raw.width < 2 || raw.height < 2) return null;
  const left = Math.max(0, raw.left);
  const top = Math.max(0, raw.top);
  const right = Math.min(raw.left + raw.width, vw);
  const bottom = Math.min(raw.top + raw.height, vh);
  const width = right - left;
  const height = bottom - top;
  if (width < 2 || height < 2) return null;
  return { left, top, width, height };
}

export function computeComplementPanels(windowSize, excludeRects) {
  const w = windowSize.w;
  const h = windowSize.h;
  if (!excludeRects?.length || w < 4 || h < 4) return [];
  let panels = [{ left: 0, top: 0, width: w, height: h }];
  for (const raw of excludeRects) {
    const hole = clipHoleToViewport(raw, w, h);
    if (!hole) continue;
    panels = panels.flatMap((p) => subtractRectFromPanel(p, hole));
  }
  return panels.filter((p) => p.width >= 2 && p.height >= 2);
}

/**
 * @param {{ left: number, top: number, width: number, height: number }} p
 * @param {{ left: number, top: number, width: number, height: number }} hole
 */
function subtractRectFromPanel(p, hole) {
  const pr = p.left + p.width;
  const pb = p.top + p.height;
  const hr = hole.left + hole.width;
  const hb = hole.top + hole.height;
  const ix1 = Math.max(p.left, hole.left);
  const iy1 = Math.max(p.top, hole.top);
  const ix2 = Math.min(pr, hr);
  const iy2 = Math.min(pb, hb);
  if (ix1 >= ix2 || iy1 >= iy2) return [p];

  const out = [];
  if (iy1 > p.top) {
    out.push({ left: p.left, top: p.top, width: p.width, height: iy1 - p.top });
  }
  if (iy2 < pb) {
    out.push({ left: p.left, top: iy2, width: p.width, height: pb - iy2 });
  }
  if (ix1 > p.left) {
    out.push({ left: p.left, top: iy1, width: ix1 - p.left, height: iy2 - iy1 });
  }
  if (ix2 < pr) {
    out.push({ left: ix2, top: iy1, width: pr - ix2, height: iy2 - iy1 });
  }
  return out;
}

function TutorialBlurredComplementPanels({
  panels,
  zBackdrop = Z_BACKDROP,
  backdropBlur = "blur(12px)",
  overlayBackground = "rgba(255, 255, 255, 0.42)",
}) {
  if (!panels?.length) return null;
  return (
    <>
      {panels.map((p, i) => (
        <div
          key={`blur-${i}`}
          aria-hidden
          style={{
            position: "fixed",
            left: p.left,
            top: p.top,
            width: p.width,
            height: p.height,
            zIndex: zBackdrop,
            backdropFilter: backdropBlur,
            WebkitBackdropFilter: backdropBlur,
            background: overlayBackground,
            pointerEvents: "none",
            boxSizing: "border-box",
            transform: "translateZ(0)",
          }}
        />
      ))}
    </>
  );
}

/** Alleen klik-vangvlakken (zelfde geometrie als complement-blur). */
export function TutorialComplementPointerBlockers({ panels, zBlock = Z_BLOCK }) {
  if (!panels?.length) return null;
  return (
    <>
      {panels.map((p, i) => (
        <div
          key={`blk-${i}`}
          aria-hidden
          style={{
            position: "fixed",
            left: p.left,
            top: p.top,
            width: p.width,
            height: p.height,
            zIndex: zBlock,
            background: "transparent",
            pointerEvents: "auto",
            boxSizing: "border-box",
          }}
        />
      ))}
    </>
  );
}

/**
 * Zelfde twee lagen als mobiele tutorial: eerst wazige vlakken (pointerEvents none), daarop transparante klik-vang.
 */
export function TutorialComplementBlurStack({
  panels,
  zBackdrop = Z_BACKDROP,
  zBlock = Z_BLOCK,
  backdropBlur = "blur(12px)",
  overlayBackground = "rgba(255, 255, 255, 0.42)",
}) {
  if (!panels?.length) return null;
  return (
    <>
      <TutorialBlurredComplementPanels
        panels={panels}
        zBackdrop={zBackdrop}
        backdropBlur={backdropBlur}
        overlayBackground={overlayBackground}
      />
      <TutorialComplementPointerBlockers panels={panels} zBlock={zBlock} />
    </>
  );
}

/** Alleen dit vlak wazig maken (bijv. invoerpaneel), zonder interactie te blokkeren. */
function RegionalBlurLayer({ rect, windowSize }) {
  if (!rect || !windowSize || rect.width < 8 || rect.height < 8) return null;
  const { w: vw, h: vh } = windowSize;
  let left = Math.max(0, rect.left);
  let top = Math.max(0, rect.top);
  let width = Math.min(rect.width, vw - left);
  let height = Math.min(rect.height, vh - top);
  width = Math.max(0, width);
  height = Math.max(0, height);
  if (width < 4 || height < 4) return null;
  return (
    <div
      aria-hidden
      style={{
        position: "fixed",
        left,
        top,
        width,
        height,
        zIndex: Z_BACKDROP,
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        background: "rgba(255, 255, 255, 0.42)",
        pointerEvents: "none",
        boxSizing: "border-box",
        transform: "translateZ(0)",
        borderRadius: 12,
      }}
    />
  );
}

/**
 * @param {object} props
 * @param {boolean} props.welcomeFullBlur
 * @param {DOMRect|null} props.holeRect
 * @param {{ w: number, h: number }} props.windowSize
 * @param {'bottom'|'center'|'top'} props.cardPosition
 * @param {number} [props.cardTopPx]
 * @param {number} [props.topCardMaxHeight] — bij cardPosition top: scrollbare max. hoogte
 * @param {number} [props.topCardFixedHeight] — bij gezet: vaste hoogte (px) i.p.v. alleen maxHeight
 * @param {boolean} [props.hideCard]
 * @param {boolean} props.showContinue
 * @param {() => void} props.onContinue
 * @param {string} [props.continueLabel]
 * @param {string} props.title
 * @param {string} props.body
 * @param {boolean} props.nextDisabled
 * @param {boolean} props.showNext
 * @param {boolean} props.showPrevious
 * @param {{ label: string, onClick: () => void, className?: string }|null} props.primaryAction
 * @param {boolean} props.spotlightActive
 * @param {DOMRect|null} [props.blurRegionRect] — optioneel apart vlak wazig maken (bijv. invoerpaneel tijdens modus-tutorial)
 * @param {Array<{left:number,top:number,width:number,height:number}>|null} [props.blurExcludeRects] — alles behalve deze vlakken wazig (veld-tutorial)
 * @param {DOMRect|null} [props.cardFillRect] — kaart exact dit vlak vullen (modus summary = invoerpaneel)
 * @param {number} [props.cardBottomPx] — bottom-kaart: vaste px vanaf viewport-onderkant (boven invoerpaneel)
 * @param {number} [props.bottomCardFixedHeight] — vaste hoogte bottom-kaart (modus: zelfde bij summary en chart)
 * @param {number} [props.cardInsetPx] — horizontale marge (links/rechts) voor de kaart; default 16
 * @param {number} [props.cardMaxWidthPx] — max-breedte kaart; default 480
 * @param {string} [props.scrollHintText] — hint onder de tekst (bijv. scroll naar aflosschema)
 * @param {boolean} [props.wrapupCollapsible] — strategy wrapup: titel/uitleg inklapbaar
 * @param {boolean} [props.wrapupCollapsed]
 * @param {() => void} [props.onWrapupCollapseToggle]
 * @param {() => void} props.onNext
 * @param {() => void} props.onPrevious
 */
export default function MobilePortalTutorialOverlay({
  step,
  welcomeFullBlur,
  spotlightActive = true,
  blurRegionRect = null,
  blurExcludeRects = null,
  holeRect,
  windowSize,
  cardPosition = "bottom",
  cardFillRect = null,
  cardTopPx,
  topCardMaxHeight,
  topCardFixedHeight,
  bottomCardTranslateYPx = 0,
  cardBottomPx,
  bottomCardFixedHeight,
  cardInsetPx,
  cardMaxWidthPx,
  scrollHintText,
  wrapupCollapsible = false,
  wrapupCollapsed = false,
  onWrapupCollapseToggle,
  hideCard = false,
  title,
  body,
  nextDisabled,
  showNext = true,
  showPrevious = true,
  showContinue = false,
  onContinue = () => {},
  continueLabel = "Doorgaan",
  primaryAction = null,
  onNext,
  onPrevious = () => {},
}) {
  const portal = typeof document !== "undefined" ? document.body : null;
  const show = step != null;

  const cardBottom = useMemo(() => `max(20px, env(safe-area-inset-bottom, 0px))`, []);

  const hole = useMemo(() => {
    if (!holeRect || !windowSize) return null;
    return computeTutorialHighlightBox(holeRect, windowSize.w, windowSize.h);
  }, [holeRect, windowSize]);

  const complementPanels = useMemo(() => {
    if (!windowSize || welcomeFullBlur || !blurExcludeRects?.length) return null;
    return computeComplementPanels(windowSize, blurExcludeRects);
  }, [windowSize, blurExcludeRects, welcomeFullBlur]);

  if (!portal || !show) return null;

  const topCardConstrained =
    cardPosition === "top" &&
    ((typeof topCardFixedHeight === "number" && topCardFixedHeight > 0) ||
      (typeof topCardMaxHeight === "number" && topCardMaxHeight > 0));

  const bottomCardConstrained =
    cardPosition === "bottom" && typeof bottomCardFixedHeight === "number" && bottomCardFixedHeight > 0;

  const fillCardConstrained =
    cardPosition === "fill" &&
    cardFillRect &&
    cardFillRect.width > 8 &&
    cardFillRect.height > 8;

  const scrollConstrained = topCardConstrained || bottomCardConstrained || fillCardConstrained;

  const insetPx = typeof cardInsetPx === "number" && cardInsetPx >= 0 ? cardInsetPx : 16;
  const maxWidthPx = typeof cardMaxWidthPx === "number" && cardMaxWidthPx > 0 ? cardMaxWidthPx : 480;

  const cardBoxStyle =
    cardPosition === "fill" && fillCardConstrained
      ? {
          position: "fixed",
          left: cardFillRect.left,
          top: cardFillRect.top,
          width: cardFillRect.width,
          height: cardFillRect.height,
          zIndex: Z_CARD,
          margin: 0,
          maxWidth: "none",
          pointerEvents: "auto",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          borderRadius: 16,
          overflow: "hidden",
        }
      : cardPosition === "center"
      ? {
          position: "fixed",
          left: insetPx,
          right: insetPx,
          top: "50%",
          transform: "translateY(-50%)",
          zIndex: Z_CARD,
          maxWidth: Math.min(maxWidthPx, 400),
          margin: "0 auto",
          pointerEvents: "auto",
        }
      : cardPosition === "top"
        ? {
            position: "fixed",
            left: insetPx,
            right: insetPx,
            top:
              cardTopPx != null
                ? `max(${cardTopPx}px, env(safe-area-inset-top, 0px))`
                : "max(72px, env(safe-area-inset-top, 0px))",
            zIndex: Z_CARD,
            maxWidth: maxWidthPx,
            margin: "0 auto",
            pointerEvents: "auto",
            ...(typeof topCardFixedHeight === "number" && topCardFixedHeight > 0
              ? {
                  height: topCardFixedHeight,
                  maxHeight: topCardFixedHeight,
                  display: "flex",
                  flexDirection: "column",
                  boxSizing: "border-box",
                }
              : typeof topCardMaxHeight === "number" && topCardMaxHeight > 0
                ? {
                    maxHeight: topCardMaxHeight,
                    display: "flex",
                    flexDirection: "column",
                    boxSizing: "border-box",
                  }
                : {}),
          }
        : {
            position: "fixed",
            left: insetPx,
            right: insetPx,
            bottom: typeof cardBottomPx === "number" ? `${cardBottomPx}px` : cardBottom,
            zIndex: Z_CARD,
            maxWidth: maxWidthPx,
            margin: "0 auto",
            pointerEvents: "auto",
            ...(bottomCardConstrained
              ? {
                  height: bottomCardFixedHeight,
                  maxHeight: bottomCardFixedHeight,
                  display: "flex",
                  flexDirection: "column",
                  boxSizing: "border-box",
                }
              : {}),
            ...(bottomCardTranslateYPx
              ? { transform: `translateY(${bottomCardTranslateYPx}px)` }
              : {}),
          };

  /** Alleen welkomstscherm: volledige blur. Modus: invoerpaneel blur OF complement-blur. Veld: blurExcludeRects. */
  const showComplementBlur =
    complementPanels?.length && spotlightActive && !welcomeFullBlur && windowSize;
  const showPointerBlockers =
    !welcomeFullBlur && spotlightActive && hole && windowSize && !showComplementBlur;
  const showRegionalBlur =
    blurRegionRect && !welcomeFullBlur && windowSize && spotlightActive && !showComplementBlur;

  return createPortal(
    <>
      {scrollHintText ? (
        <style>{`
          @keyframes mobileTutorialScrollHintBounce {
            0%, 100% { transform: translateY(0); opacity: 0.92; }
            50% { transform: translateY(6px); opacity: 1; }
          }
        `}</style>
      ) : null}
      {welcomeFullBlur && <FullBlurLayer />}
      {showComplementBlur && <TutorialComplementBlurStack panels={complementPanels} />}
      {showRegionalBlur && <RegionalBlurLayer rect={blurRegionRect} windowSize={windowSize} />}
      {showPointerBlockers && <SharpPointerBlockers hole={hole} windowSize={windowSize} />}

      {!hideCard && (
        <div style={cardBoxStyle}>
          <div
            style={{
              background: "#fff",
              borderRadius: 16,
              padding: "18px 18px 16px",
              boxShadow: "0 18px 48px rgba(0,0,0,0.2)",
              border: "1px solid #e5e7eb",
              ...(scrollConstrained
                ? {
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                  }
                : {}),
            }}
          >
            <div
              style={
                scrollConstrained
                  ? {
                      overflowY: "auto",
                      WebkitOverflowScrolling: "touch",
                      flex: 1,
                      minHeight: 0,
                      marginBottom: 12,
                    }
                  : {}
              }
            >
              {wrapupCollapsible ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 12,
                    marginBottom: wrapupCollapsed ? 8 : 10,
                  }}
                >
                  <h2
                    style={{
                      margin: 0,
                      fontSize: 18,
                      fontWeight: 700,
                      color: "#111827",
                      flex: 1,
                      minWidth: 0,
                      paddingRight: 8,
                      lineHeight: 1.3,
                    }}
                  >
                    {title}
                  </h2>
                  {onWrapupCollapseToggle ? (
                    <button
                      type="button"
                      className="tutorial-target-pulse"
                      aria-label={wrapupCollapsed ? "Uitleg tonen" : "Uitleg verbergen"}
                      onClick={onWrapupCollapseToggle}
                      style={{
                        flexShrink: 0,
                        border: "none",
                        background: "transparent",
                        cursor: "pointer",
                        fontSize: 28,
                        fontWeight: 300,
                        lineHeight: 1,
                        padding: "2px 6px",
                        color: "#374151",
                        fontFamily: "inherit",
                      }}
                    >
                      {wrapupCollapsed ? "+" : "−"}
                    </button>
                  ) : null}
                </div>
              ) : (
                <h2 style={{ margin: "0 0 10px", fontSize: 18, fontWeight: 700, color: "#111827" }}>{title}</h2>
              )}
              {!wrapupCollapsible || !wrapupCollapsed ? (
                <>
                  <p
                    style={{
                      margin: scrollConstrained ? "0 0 0" : scrollHintText ? "0 0 10px" : "0 0 16px",
                      fontSize: 15,
                      lineHeight: 1.55,
                      color: "#4b5563",
                    }}
                  >
                    {body}
                  </p>
                  {scrollHintText ? (
                    <div
                      role="note"
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 8,
                        marginBottom: scrollConstrained ? 8 : 14,
                        paddingTop: 2,
                      }}
                    >
                      <span
                        aria-hidden
                        style={{
                          fontSize: 24,
                          lineHeight: 1,
                          color: "#3399FF",
                          animation: "mobileTutorialScrollHintBounce 1.35s ease-in-out infinite",
                        }}
                      >
                        ↓
                      </span>
                      <span
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: "#374151",
                          textAlign: "center",
                          lineHeight: 1.45,
                        }}
                      >
                        {scrollHintText}
                      </span>
                    </div>
                  ) : null}
                </>
              ) : null}
            </div>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: primaryAction ? 12 : 10,
                justifyContent: primaryAction ? "stretch" : "flex-end",
                alignItems: "center",
                flexDirection: primaryAction ? "column" : "row",
                ...(scrollConstrained ? { flexShrink: 0 } : {}),
              }}
            >
              {primaryAction ? (
                <>
                  <button
                    type="button"
                    onClick={primaryAction.onClick}
                    className={primaryAction.className}
                    style={{
                      padding: "12px 20px",
                      borderRadius: 9999,
                      border: "none",
                      background: "#3399FF",
                      color: "#fff",
                      fontSize: 15,
                      fontWeight: 600,
                      cursor: "pointer",
                      width: "100%",
                    }}
                  >
                    {primaryAction.label}
                  </button>
                  {showPrevious && (
                    <button
                      type="button"
                      onClick={onPrevious}
                      style={{
                        padding: "8px 12px",
                        borderRadius: 9999,
                        border: "none",
                        background: "transparent",
                        color: "#6b7280",
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: "pointer",
                        width: "100%",
                      }}
                    >
                      Vorige
                    </button>
                  )}
                </>
              ) : (
                <>
                  {showPrevious && (
                    <button
                      type="button"
                      onClick={onPrevious}
                      style={{
                        padding: "10px 16px",
                        borderRadius: 9999,
                        border: "1px solid #d1d5db",
                        background: "#f9fafb",
                        color: "#374151",
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                    >
                      Vorige
                    </button>
                  )}
                  {showContinue && (
                    <button
                      type="button"
                      onClick={onContinue}
                      style={{
                        padding: "10px 20px",
                        borderRadius: 9999,
                        border: "none",
                        background: "#3399FF",
                        color: "#fff",
                        fontSize: 15,
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                    >
                      {continueLabel}
                    </button>
                  )}
                  {showNext && (
                    <button
                      type="button"
                      onClick={onNext}
                      disabled={nextDisabled}
                      style={{
                        padding: "10px 20px",
                        borderRadius: 9999,
                        border: "none",
                        background: nextDisabled ? "#93c5fd" : "#3399FF",
                        color: "#fff",
                        fontSize: 15,
                        fontWeight: 600,
                        cursor: nextDisabled ? "not-allowed" : "pointer",
                        opacity: nextDisabled ? 0.85 : 1,
                      }}
                    >
                      Volgende
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>,
    portal
  );
}
